import type { AdventureCategoryDto } from '@api/types/adventureCategory'

// Used for a category with no Color, or one no row knows
export const FALLBACK_CATEGORY_COLOR = '#666666'

const normaliseHex = (color: string | null | undefined): string | null => {
  const hex = color?.trim().replace(/^#/, '').toLowerCase()
  if (!hex || !/^([0-9a-f]{3}|[0-9a-f]{6})$/.test(hex)) return null
  return '#' + (hex.length === 3 ? hex.replace(/./g, '$&$&') : hex)
}

// Rotates a colour's hue by `degrees`, keeping its saturation and lightness
const rotateHue = (hex: string, degrees: number): string => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  let h = 0
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6)
    else if (max === g) h = 60 * ((b - r) / d + 2)
    else h = 60 * ((r - g) / d + 4)
  }
  h = (((h + degrees) % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  const [r1, g1, b1] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]
  return '#' + [r1, g1, b1].map((v) => Math.round((v + m) * 255).toString(16).padStart(2, '0')).join('')
}

/**
 * Chart colour per category id: each row's own Color, except that a row whose colour is already
 * taken by a lower id gets that colour turned round the hue wheel until it is free, so two
 * categories sharing a Color (Trails and Supporting Business both use orange) stay apart on a
 * chart. Ids decide who keeps the colour, so reordering categories never swaps chart colours.
 */
export const buildCategoryColors = (categories: AdventureCategoryDto[]): Map<number, string> => {
  const rows = [...categories].sort((a, b) => a.id - b.id)
  // Every colour a row asks for is reserved, so a shifted colour never lands on another row's own
  const reserved = new Set(rows.map((c) => normaliseHex(c.color)).filter((c): c is string => c !== null))
  const used = new Set<string>()
  const colors = new Map<number, string>()

  for (const row of rows) {
    const own = normaliseHex(row.color)
    let color = own ?? FALLBACK_CATEGORY_COLOR
    if (own && used.has(own)) {
      for (let step = 1; step < 12; step++) {
        const candidate = rotateHue(own, step * 30)
        if (!used.has(candidate) && !reserved.has(candidate)) {
          color = candidate
          break
        }
      }
    }
    used.add(color)
    colors.set(row.id, color)
  }
  return colors
}

// Chart theme colors - work well in both light and dark modes
export const CHART_COLORS = {
  activeUsers: '#8B1538', // Primary brand color
  newUsers: '#4CAF50', // Success green
  soloCompletions: '#2196F3', // Blue
  groupCompletions: '#FF9800', // Orange
  grid: '#e0e0e0',
  gridDark: '#424242',
  axis: '#666666',
  axisDark: '#9e9e9e',
}
